import { useMemo, useState } from "react";
import { useResource, formatDateTime } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { type QuarantineRow, streamOf } from "../../lib/intakeStreams";
import { senderOf, detailsOf, previousRefNote, containsLink } from "../../lib/intakeDisplay";

/**
 * The Applicants queue — people applying to work with us: talent pool
 * registrations, job applications and expressions of interest. Each form keeps
 * its own group, because a speculative talent-pool entry is not the same thing
 * as an application against an advertised role.
 *
 * These are applicants, NOT employees: nothing here creates a staff record or
 * a login. Hiring someone is a separate, deliberate step — the Onboarding
 * stage — so the only actions here are reading a submission and closing it
 * with a reason. Rows share the quarantine table with the other intake
 * streams; only the presentation and routing differ.
 */

export const ApplicantsQueue = () => {
    const { user } = useAuth();
    const canDecide = user?.role === "admin" || user?.role === "manager";

    const quarantine = useResource<QuarantineRow[]>(canDecide ? "/intake/quarantine" : null);
    const [closing, setClosing] = useState<QuarantineRow | null>(null);
    const [open, setOpen] = useState<string | null>(null);

    const applicants = useMemo(
        () => (quarantine.data ?? []).filter((q) => streamOf(q.form_name) === "recruitment"),
        [quarantine.data]
    );

    const groups = useMemo(() => {
        const byForm = new Map<string, QuarantineRow[]>();
        for (const row of applicants) {
            const key = (row.form_name ?? "unknown").toLowerCase();
            byForm.set(key, [...(byForm.get(key) ?? []), row]);
        }
        return [...byForm.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [applicants]);

    if (!canDecide) return <div className="error-banner">This page is for admins and managers.</div>;
    if (quarantine.error) return <div className="error-banner">{quarantine.error}</div>;

    return (
        <div style={{ display: "grid", gap: "1rem" }}>
            {closing && (
                <ConfirmDialog
                    title={`Close this ${closing.form_name ?? "application"}?`}
                    message="It stays in the quarantine record with your reason — nothing is deleted."
                    confirmLabel="Close" requireReason
                    reasonLabel="Reason (not suitable, position filled, contacted, duplicate…)"
                    request={{ method: "post", path: `/intake/quarantine/${closing.id}/reject` }}
                    onClose={() => setClosing(null)}
                    onDone={() => { setClosing(null); quarantine.reload(); }} />
            )}

            {groups.length === 0 && (
                <div className="card"><div className="card-body empty">
                    No applications waiting. Talent pool registrations, job applications and
                    expressions of interest from the website arrive here.
                </div></div>
            )}

            {groups.map(([form, rows]) => (
                <div key={form} className="card">
                    <div className="card-head">
                        <h2 style={{ textTransform: "capitalize" }}>{form.replace(/[-_]+/g, " ")}</h2>
                        <span className="chip info">{rows.length} pending</span>
                    </div>
                    <div className="card-body">
                        <div style={{ display: "grid", gap: "0.5rem" }}>
                            {rows.map((item) => (
                                <div key={item.id} className="file-row" style={{ alignItems: "flex-start" }}>
                                    <span className="mono" style={{ whiteSpace: "nowrap" }}>{formatDateTime(item.received_at)}</span>
                                    <span style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 600 }}>
                                            {item.ref_code && (
                                                <span className="chip mute mono" style={{ marginRight: 6, fontWeight: 700 }}
                                                    title={previousRefNote(item.previous_ref_code)}>{item.ref_code}</span>
                                            )}
                                            {senderOf(item.payload)}
                                            {containsLink(item.payload) && (
                                                <span className="chip warn" style={{ marginLeft: 6 }}
                                                    title="This submission contains a web address. It is shown as plain text and is not clickable — do not open it unless you trust the sender.">
                                                    Contains a link
                                                </span>
                                            )}
                                        </div>
                                        {open === item.id && (
                                            <dl className="kv" style={{ marginTop: "0.5rem" }}>
                                                {detailsOf(item.payload).map(([k, v]) => (
                                                    <span key={k} style={{ display: "contents" }}>
                                                        <dt style={{ textTransform: "capitalize" }}>{k}</dt>
                                                        <dd style={{ whiteSpace: "pre-wrap" }}>{v}</dd>
                                                    </span>
                                                ))}
                                            </dl>
                                        )}
                                    </span>
                                    <span style={{ display: "flex", gap: "0.35rem", whiteSpace: "nowrap" }}>
                                        <button className="icon-btn" onClick={() => setOpen(open === item.id ? null : item.id)}>
                                            {open === item.id ? "Hide details" : "Details"}
                                        </button>
                                        <button className="icon-btn danger" onClick={() => setClosing(item)}>Close</button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};
